import { Link } from "react-router";
import { Pagination } from "#app/components/navigation/Pagination.tsx";
import { VoteButtons } from "#app/components/vote-buttons.tsx";
import { type Route } from "./+types/definitions.index.ts";
import { type Route as definitionsRoute } from "./+types/definitions.ts";

export default function AuthorDefinitionsIndexRoute({
  matches,
}: Route.ComponentProps) {
  const definitionsMatch = matches.find(
    (m) => m?.id === "routes/authors+/$username_+/definitions",
  ) as { data: definitionsRoute.ComponentProps["loaderData"] } | undefined;

  if (!definitionsMatch?.data) {
    return <div>Loading...</div>;
  }

  const { definitions, pagination, author } = definitionsMatch.data;

  if (definitions.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-body-md text-muted-foreground mb-4">
          <svg
            className="mx-auto h-12 w-12"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
            />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-foreground mb-2">
          No definitions yet
        </h3>
        <p className="text-gray-600 dark:text-muted-foreground">
          This author hasn't added any definitions yet.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Definitions Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {definitions.map((definition) => (
          <div
            key={definition.id}
            className="bg-card rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-200 dark:border-border"
          >
            <div className="p-6">
              <Link
                to={`/words/${definition.word.slug}`}
                className="block group"
              >
                <h3
                  className="text-xl font-semibold text-card-foreground group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors mb-3"
                  style={{ fontFamily: "var(--serif-head)" }}
                >
                  {definition.word.term}
                </h3>
                <p
                  className="text-card-foreground text-body-md mb-4 line-clamp-3"
                  style={{ fontFamily: "var(--serif)" }}
                >
                  {definition.translationEn}
                </p>
                {definition.usageShona && (
                  <p
                    className="text-sm text-card-foreground italic mb-4 line-clamp-2"
                    style={{ fontFamily: "var(--serif)" }}
                  >
                    "{definition.usageShona}"
                  </p>
                )}
              </Link>

              <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-border">
                <VoteButtons
                  up={definition.up}
                  down={definition.down}
                  size="sm"
                />
                <span className="text-xs text-gray-400 dark:text-muted-foreground">
                  {new Date(definition.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex justify-center pt-8">
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            basePath={`/authors/${author.username}/definitions`}
          />
        </div>
      )}
    </div>
  );
}

export const meta: Route.MetaFunction = ({ params, matches }) => {
  const definitionsMatch = matches.find(
    (m) => m?.id === "routes/authors+/$username_+/definitions",
  ) as { data: definitionsRoute.ComponentProps["loaderData"] } | undefined;

  const displayName =
    definitionsMatch?.data?.author.displayName ?? params.username;
  const definitionCount = definitionsMatch?.data?.pagination.totalCount ?? 0;
  const definitionsText = definitionCount === 1 ? "definition" : "definitions";

  return [
    {
      title: `${displayName}'s ${definitionCount} ${definitionsText} | shonaslang`,
    },
    {
      name: "description",
      content: `Browse all ${definitionCount} Shona slang ${definitionsText} contributed by ${displayName} on shonaslang`,
    },
  ];
};
